import React, { useState } from "react";

const AddListingPage = ({ onSave, onCancel, editListing = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(editListing || {
    Price: '',
    Room_Type: '',
    Year_of_Construction: '',
    Empty_or_Not: 'Sale',
    No_of_Floors: '',
    Contact_Info: '',
    Area_of_House: '',
    Area_of_Land: '',
    Address: '',
    Link_to_Website: '',
    Images: [],
    Coordinates: [],
    featured: false,
    supportingDocType: 'none',
    supportingDocContent: '',
    description: ''
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const totalSteps = 4;

  const generatePropertyId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `RE-${timestamp}-${random}`.toUpperCase();
  };

  const handleImageUpload = (files) => {
    const fileArray = Array.from(files);
    setImageFiles(prev => [...prev, ...fileArray]);
    
    fileArray.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          Images: [...prev.Images, e.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      Images: prev.Images.filter((_, i) => i !== index)
    }));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdfFile(file);
      setFormData(prev => ({
        ...prev,
        supportingDocContent: file.name
      }));
    }
  };

  const handleSubmit = () => {
    const finalData = {
      ...formData,
      property_id: editListing ? editListing.property_id : generatePropertyId(),
      Coordinates: formData.Coordinates.length > 0 ? formData.Coordinates : [35.6762, 139.6503]
    };
    
    onSave(finalData);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.Address && formData.Price && formData.Year_of_Construction;
      case 2:
        return formData.Area_of_House && formData.Contact_Info;
      case 3:
        return true; // Images are optional
      case 4:
        return true; // Final review
      default:
        return false;
    }
  };

  // Step 1: Basic Information
  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="dark-header !text-[#000] !mb-4">Basic Property Information</h2>
        <p className="light-header">Let's start with the essential details about your property</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Address & Location</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Property Address *</label>
              <input 
                type="text"
                value={formData.Address}
                onChange={(e) => setFormData({...formData, Address: e.target.value})}
                placeholder="e.g., Hinohara, Nishitama District, Tokyo"
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold mb-3 text-[#000]">Latitude</label>
                <input 
                  type="number"
                  step="any"
                  value={formData.Coordinates[0] || ''}
                  onChange={(e) => setFormData({...formData, Coordinates: [parseFloat(e.target.value) || 0, formData.Coordinates[1] || 0]})}
                  placeholder="35.7268216"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
                />
              </div>
              
              <div>
                <label className="block font-semibold mb-3 text-[#000]">Longitude</label>
                <input 
                  type="number"
                  step="any"
                  value={formData.Coordinates[1] || ''}
                  onChange={(e) => setFormData({...formData, Coordinates: [formData.Coordinates[0] || 0, parseFloat(e.target.value) || 0]})}
                  placeholder="139.1488603"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Pricing & Status</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Price *</label>
              <div className="relative">
                <span className="absolute left-4 top-4 text-[#10c9c0] font-semibold">¥</span>
                <input 
                  type="text"
                  value={formData.Price}
                  onChange={(e) => setFormData({...formData, Price: e.target.value})}
                  placeholder="6000000 yen"
                  className="w-full pl-8 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
                />
              </div>
            </div>
            
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Property Status</label>
              <select 
                value={formData.Empty_or_Not}
                onChange={(e) => setFormData({...formData, Empty_or_Not: e.target.value})}
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              >
                <option value="Sale">For Sale</option>
                <option value="Rental">For Rental</option>
                <option value="Sold">Sold</option>
                <option value="Rented">Rented</option>
              </select>
            </div>
            
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Year of Construction *</label>
              <input 
                type="number"
                value={formData.Year_of_Construction}
                onChange={(e) => setFormData({...formData, Year_of_Construction: e.target.value})}
                placeholder="1972"
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Property Details
  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="dark-header !text-[#000] !mb-4">Property Specifications</h2>
        <p className="light-header">Tell us more about the property features and contact details</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Property Features</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Room Type</label>
              <select 
                value={formData.Room_Type || ''}
                onChange={(e) => setFormData({...formData, Room_Type: e.target.value})}
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              >
                <option value="">Select Room Type</option>
                <option value="1K">1K</option>
                <option value="1DK">1DK</option>
                <option value="1LDK">1LDK</option>
                <option value="2K">2K</option>
                <option value="2DK">2DK</option>
                <option value="2LDK">2LDK</option>
                <option value="3K">3K</option>
                <option value="3DK">3DK</option>
                <option value="3LDK">3LDK</option>
                <option value="4LDK+">4LDK+</option>
                <option value="House">House</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold mb-3 text-[#000]">Number of Floors</label>
                <input 
                  type="number"
                  value={formData.No_of_Floors}
                  onChange={(e) => setFormData({...formData, No_of_Floors: e.target.value})}
                  placeholder="2"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
                />
              </div>
              
              <div>
                <label className="block font-semibold mb-3 text-[#000]">Area of House *</label>
                <input 
                  type="text"
                  value={formData.Area_of_House}
                  onChange={(e) => setFormData({...formData, Area_of_House: e.target.value})}
                  placeholder="23 m²"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
                />
              </div>
            </div>
            
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Area of Land</label>
              <input 
                type="text"
                value={formData.Area_of_Land}
                onChange={(e) => setFormData({...formData, Area_of_Land: e.target.value})}
                placeholder="23.0 m²"
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Contact & Description</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Contact Information *</label>
              <input 
                type="text"
                value={formData.Contact_Info}
                onChange={(e) => setFormData({...formData, Contact_Info: e.target.value})}
                placeholder="Tel: 042-596-0805"
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              />
            </div>
            
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Property Description</label>
              <textarea 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the property, its features, and any special characteristics..."
                rows="6"
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white resize-none"
              />
            </div>
            
            <div className="flex items-center p-4 bg-gradient-to-r from-[#10c9c0]/10 to-[#0eb1a9]/10 rounded-xl border-2 border-dashed border-[#10c9c0]/20">
              <input 
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                className="mr-3 w-5 h-5 text-[#10c9c0] rounded"
              />
              <label htmlFor="featured" className="font-semibold text-[#000] flex items-center">
                <i className="fas fa-star mr-2 text-[#10c9c0]"></i>
                Make this a featured listing
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Images & Documents
  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="dark-header !text-[#000] !mb-4">Images & Supporting Documents</h2>
        <p className="light-header">Showcase your property with high-quality images and documents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Upload */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Property Images</h3>
          
          <div 
            className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
              dragActive 
                ? 'border-[#10c9c0] bg-[#10c9c0]/5' 
                : 'border-gray-300 hover:border-[#10c9c0] hover:bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-cloud-upload-alt text-white text-2xl"></i>
            </div>
            <h4 className="font-semibold text-[#000] mb-2">Drag & drop images here</h4>
            <p className="text-[#949995] mb-4">or click to browse files</p>
            <input 
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files)}
              className="hidden"
              id="image-upload"
            />
            <label 
              htmlFor="image-upload"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white rounded-xl hover:from-[#0eb1a9] hover:to-[#10c9c0] transition-all cursor-pointer"
            >
              <i className="fas fa-plus mr-2"></i>
              Choose Images
            </label>
          </div>
          
          {formData.Images && formData.Images.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold mb-4 text-[#000]">Uploaded Images ({formData.Images.length})</h4>
              <div className="grid grid-cols-2 gap-4">
                {formData.Images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={image} 
                      alt={`Property ${index + 1}`}
                      className="w-full h-32 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <i className="fas fa-times text-sm"></i>
                    </button>
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                      Image {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Supporting Documents */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="dark-header !text-[#000] !mb-6 !text-xl">Supporting Documents</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-3 text-[#000]">Document Type</label>
              <select 
                value={formData.supportingDocType}
                onChange={(e) => setFormData({...formData, supportingDocType: e.target.value, supportingDocContent: ''})}
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-gray-50 focus:bg-white"
              >
                <option value="none">No Supporting Document</option>
                <option value="link">Website Link</option>
                <option value="pdf">PDF Upload</option>
              </select>
            </div>
            
            {formData.supportingDocType === 'link' && (
              <div className="bg-blue-50 p-6 rounded-xl">
                <label className="block font-semibold mb-3 text-[#000]">Website Link</label>
                <input 
                  type="url"
                  value={formData.Link_to_Website}
                  onChange={(e) => setFormData({...formData, Link_to_Website: e.target.value})}
                  placeholder="https://example.com/property-details"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                />
                <p className="text-sm text-[#949995] mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  Add a link to additional property information
                </p>
              </div>
            )}
            
            {formData.supportingDocType === 'pdf' && (
              <div className="bg-red-50 p-6 rounded-xl">
                <label className="block font-semibold mb-3 text-[#000]">Upload PDF</label>
                <input 
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all bg-white"
                />
                {pdfFile && (
                  <div className="mt-4 flex items-center p-3 bg-white rounded-lg border">
                    <i className="fas fa-file-pdf text-red-500 text-xl mr-3"></i>
                    <div>
                      <p className="font-medium text-[#000]">{pdfFile.name}</p>
                      <p className="text-sm text-[#949995]">{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                )}
                <p className="text-sm text-[#949995] mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  Upload floor plans, certificates, or other documents
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Step 4: Review & Submit
  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="dark-header !text-[#000] !mb-4">Review Your Listing</h2>
        <p className="light-header">Please review all information before submitting</p>
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Property Info */}
          <div className="space-y-6">
            <div>
              <h3 className="dark-header !text-[#000] !mb-4 !text-lg">Property Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Address:</span>
                  <span className="text-[#000]">{formData.Address}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Price:</span>
                  <span className="text-[#10c9c0] font-bold">¥{formData.Price}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Status:</span>
                  <span className="text-[#000]">{formData.Empty_or_Not}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Year Built:</span>
                  <span className="text-[#000]">{formData.Year_of_Construction}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Room Type:</span>
                  <span className="text-[#000]">{formData.Room_Type || 'Not specified'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">House Area:</span>
                  <span className="text-[#000]">{formData.Area_of_House}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Land Area:</span>
                  <span className="text-[#000]">{formData.Area_of_Land || 'Not specified'}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="dark-header !text-[#000] !mb-4 !text-lg">Contact & Features</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Contact:</span>
                  <span className="text-[#000]">{formData.Contact_Info}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Floors:</span>
                  <span className="text-[#000]">{formData.No_of_Floors || 'Not specified'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-[#949995]">Featured:</span>
                  <span className={formData.featured ? 'text-[#10c9c0]' : 'text-[#949995]'}>
                    {formData.featured ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Images & Documents */}
          <div className="space-y-6">
            <div>
              <h3 className="dark-header !text-[#000] !mb-4 !text-lg">Images ({formData.Images.length})</h3>
              {formData.Images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {formData.Images.slice(0, 6).map((image, index) => (
                    <img 
                      key={index}
                      src={image} 
                      alt={`Property ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg"
                    />
                  ))}
                  {formData.Images.length > 6 && (
                    <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-[#949995] text-sm">+{formData.Images.length - 6} more</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[#949995] italic">No images uploaded</p>
              )}
            </div>

            <div>
              <h3 className="dark-header !text-[#000] !mb-4 !text-lg">Supporting Documents</h3>
              {formData.supportingDocType === 'none' && (
                <p className="text-[#949995] italic">No supporting documents</p>
              )}
              {formData.supportingDocType === 'link' && formData.Link_to_Website && (
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <i className="fas fa-link text-blue-500 mr-3"></i>
                  <div>
                    <p className="font-medium text-[#000]">Website Link</p>
                    <p className="text-sm text-blue-600 break-all">{formData.Link_to_Website}</p>
                  </div>
                </div>
              )}
              {formData.supportingDocType === 'pdf' && pdfFile && (
                <div className="flex items-center p-3 bg-red-50 rounded-lg">
                  <i className="fas fa-file-pdf text-red-500 mr-3"></i>
                  <div>
                    <p className="font-medium text-[#000]">PDF Document</p>
                    <p className="text-sm text-[#949995]">{pdfFile.name}</p>
                  </div>
                </div>
              )}
            </div>

            {formData.description && (
              <div>
                <h3 className="dark-header !text-[#000] !mb-4 !text-lg">Description</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-[#000]">{formData.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fafafa]" style={{ fontFamily: '"Libre Franklin", sans-serif' }}>
      {/* FontAwesome CDN */}
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
        integrity="sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw==" 
        crossOrigin="anonymous" 
        referrerPolicy="no-referrer" 
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="f-section mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button 
                onClick={onCancel}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
              >
                <i className="fas fa-arrow-left text-gray-600"></i>
              </button>
              <div>
                <h1 className="dark-header !text-[#000] !mb-1">
                  {editListing ? 'Edit Listing' : 'Add New Listing'}
                </h1>
                <p className="light-header !mb-0">Step {currentStep} of {totalSteps}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      step === currentStep
                        ? 'bg-[#10c9c0] text-white'
                        : step < currentStep
                        ? 'bg-[#10c9c0] text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step < currentStep ? (
                      <i className="fas fa-check text-xs"></i>
                    ) : (
                      step
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="f-section mx-auto px-4">
          <div className="h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="f-section mx-auto px-4 py-12">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="f-section mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Previous
            </button>

            <div className="flex items-center space-x-4">
              <button
                onClick={onCancel}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>

              {currentStep < totalSteps ? (
                <button
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className={`flex items-center px-8 py-3 rounded-xl transition-all font-medium ${
                    isStepValid()
                      ? 'bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white hover:from-[#0eb1a9] hover:to-[#10c9c0] shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next Step
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="flex items-center px-8 py-3 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white rounded-xl hover:from-[#0eb1a9] hover:to-[#10c9c0] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  <i className="fas fa-save mr-2"></i>
                  {editListing ? 'Update Listing' : 'Create Listing'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom padding to prevent content being hidden behind fixed footer */}
      <div className="h-24"></div>
    </div>
  );
};

export default AddListingPage;